'use client'

import React, { Component, ErrorInfo, ReactNode } from 'react'
import Button from './ui/Button'
import Card from './ui/Card'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  showDiagnostics?: boolean
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
  errorId: string
  timestamp: string
  userAgent: string
  url: string
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      timestamp: '',
      userAgent: '',
      url: '',
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    const timestamp = new Date().toISOString()
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown'
    const url = typeof window !== 'undefined' ? window.location.href : 'Unknown'

    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId,
      timestamp,
      userAgent,
      url,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { errorId, timestamp, userAgent, url } = this.state

    // Enhanced error logging with diagnostics
    console.group(`🚨 ErrorBoundary caught an error - ${errorId}`)
    console.error('Error:', error)
    console.error('Error Info:', errorInfo)
    console.error('Timestamp:', timestamp)
    console.error('URL:', url)
    console.error('User Agent:', userAgent)
    console.error('Component Stack:', errorInfo.componentStack)
    console.error('Error Stack:', error.stack)
    console.groupEnd()

    // Always log to external service for full diagnostics
    this.logErrorToService(error, errorInfo, {
      errorId,
      timestamp,
      userAgent,
      url,
    })

    this.setState({
      error,
      errorInfo,
    })
  }

  private logErrorToService = async (
    error: Error, 
    errorInfo: ErrorInfo, 
    context: { errorId: string; timestamp: string; userAgent: string; url: string }
  ) => {
    try {
      const errorData = {
        errorId: context.errorId,
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: context.timestamp,
        url: context.url,
        userAgent: context.userAgent,
        environment: process.env.NODE_ENV,
      }

      console.log('Sending error to logging service:', errorData)
      
      // Send to our error logging API
      await fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData)
      })
    } catch (loggingError) {
      console.error('Failed to log error to service:', loggingError)
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      timestamp: '',
      userAgent: '',
      url: '',
    })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleCopyErrorDetails = () => {
    const { error, errorInfo, errorId, timestamp, userAgent, url } = this.state
    const errorDetails = {
      errorId,
      timestamp,
      url,
      userAgent,
      environment: process.env.NODE_ENV,
      error: {
        name: error?.name,
        message: error?.message,
        stack: error?.stack,
        fullError: error ? JSON.parse(JSON.stringify(error, Object.getOwnPropertyNames(error))) : null,
      },
      errorInfo: errorInfo ? JSON.parse(JSON.stringify(errorInfo, Object.getOwnPropertyNames(errorInfo))) : null,
      componentStack: errorInfo?.componentStack,
    }

    navigator.clipboard?.writeText(JSON.stringify(errorDetails, null, 2))
      .then(() => {
        console.log('Error details copied to clipboard')
      })
      .catch((err) => {
        console.error('Failed to copy error details:', err)
      })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      const { error, errorInfo, errorId, timestamp, url, userAgent } = this.state
      const showDiagnostics = this.props.showDiagnostics !== false // Always show unless explicitly disabled

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
          <Card className="text-center max-w-4xl w-full">
            <div className="text-6xl mb-4" role="img" aria-label="Error">
              😞
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-4">
              We&apos;re sorry for the inconvenience. The application encountered an unexpected error.
            </p>

            {/* Error ID for support */}
            <div className="mb-6 p-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Error ID:</strong> <code className="bg-gray-200 px-2 py-1 rounded">{errorId}</code>
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Please include this ID when contacting support.
              </p>
            </div>
            
            {/* Enhanced error details - always shown for full diagnostics */}
            {showDiagnostics && error && (
              <div className="mb-6 text-left">
                <details className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <summary className="cursor-pointer font-semibold text-red-900 mb-2 flex items-center gap-2">
                    <span>Error Details</span>
                    <button
                      onClick={this.handleCopyErrorDetails}
                      className="text-xs bg-red-100 hover:bg-red-200 px-2 py-1 rounded"
                      title="Copy error details to clipboard"
                    >
                      📋 Copy
                    </button>
                  </summary>
                  <div className="space-y-4">
                    <div>
                      <strong className="text-red-800">Error Message:</strong>
                      <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto">
                        {error?.message || 'No error message available'}
                      </pre>
                    </div>
                    
                    <div>
                      <strong className="text-red-800">Error Name:</strong>
                      <pre className="text-sm text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto">
                        {error?.name || 'Unknown'}
                      </pre>
                    </div>
                    
                    <div>
                      <strong className="text-red-800">Error Stack:</strong>
                      <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-40">
                        {error?.stack || 'No stack trace available'}
                      </pre>
                    </div>
                    
                    {errorInfo?.componentStack && (
                      <div>
                        <strong className="text-red-800">Component Stack:</strong>
                        <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-40">
                          {errorInfo.componentStack}
                        </pre>
                      </div>
                    )}
                    
                    <div>
                      <strong className="text-red-800">Full Error Object:</strong>
                      <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-40">
                        {JSON.stringify(error, Object.getOwnPropertyNames(error), 2)}
                      </pre>
                    </div>
                    
                    {errorInfo && (
                      <div>
                        <strong className="text-red-800">Full Error Info:</strong>
                        <pre className="text-xs text-red-700 mt-1 p-2 bg-red-100 rounded overflow-auto max-h-40">
                          {JSON.stringify(errorInfo, Object.getOwnPropertyNames(errorInfo), 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>

                <details className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <summary className="cursor-pointer font-semibold text-blue-900 mb-2">
                    System Information
                  </summary>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong className="text-blue-800">Timestamp:</strong>
                      <p className="text-blue-700">{timestamp}</p>
                    </div>
                    <div>
                      <strong className="text-blue-800">URL:</strong>
                      <p className="text-blue-700 break-all">{url}</p>
                    </div>
                    <div className="md:col-span-2">
                      <strong className="text-blue-800">User Agent:</strong>
                      <p className="text-blue-700 text-xs break-all">{userAgent}</p>
                    </div>
                  </div>
                </details>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={this.handleReset} variant="secondary">
                Try Again
              </Button>
              <Button onClick={this.handleReload}>
                Reload Page
              </Button>
            </div>

            <p className="mt-6 text-sm text-gray-500">
              If this problem persists, please contact support with the error ID above.
            </p>
          </Card>
        </div>
      )
    }

    return this.props.children
  }
}

